import { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import { Navbar } from '@/components/homepage/Navbar';
import Footer from '@/components/homepage/Footer';
import { enhancedInputValidator } from '@/utils/enhancedInputValidator';
import { translateSupabaseError } from '@/utils/supabaseErrorTranslator';
import { supabase } from '@/integrations/supabase/client';
import { AlertTriangle, Mail } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { getLocalizedPath } from '@/utils/languageUtils';

const SignIn = () => {
  const { signIn, isAuthenticated, loading, userRole } = useAuth();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null);
  const pageTopRef = useRef<HTMLDivElement>(null);

  // Confirmation error state (from failed link in URL)
  const [confirmationError, setConfirmationError] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendErrorKey, setResendErrorKey] = useState<string | null>(null);
  const [resendErrorMsg, setResendErrorMsg] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [isCountdownActive, setIsCountdownActive] = useState(true);

  // Unconfirmed email panel state (from login attempt)
  const [unconfirmedPanel, setUnconfirmedPanel] = useState<{
    visible: boolean;
    email: string;
    recentlySent: boolean;
    secondsUntilResend: number;
  }>({ visible: false, email: '', recentlySent: false, secondsUntilResend: 0 });
  const [unconfirmedResendLoading, setUnconfirmedResendLoading] = useState(false);
  const [unconfirmedResendSuccess, setUnconfirmedResendSuccess] = useState(false);
  const [unconfirmedResendErrorKey, setUnconfirmedResendErrorKey] = useState<string | null>(null);
  const [unconfirmedResendErrorMsg, setUnconfirmedResendErrorMsg] = useState<string | null>(null);
  const [unconfirmedCountdown, setUnconfirmedCountdown] = useState(0);
  const [isUnconfirmedCountdownActive, setIsUnconfirmedCountdownActive] = useState(false);

  const signInSchema = z.object({
    email: z.string()
      .email({ message: t('auth.emailValidationError') })
      .max(100, { message: t('auth.emailMaxLength') })
      .transform((val) => enhancedInputValidator.sanitizeText(val, 100))
      .refine((val) => {
        const validation = enhancedInputValidator.validateEmail(val);
        return validation.isValid;
      }, { message: t('auth.emailValidationError') }),
    password: z.string()
      .min(1, { message: t('auth.mandatoryField') })
      .max(128, { message: t('auth.passwordMaxLength') })
      .refine((val) => {
        const validation = enhancedInputValidator.validatePasswordForAuthentication(val, 128);
        return validation.isValid;
      }, { message: t('auth.suspiciousContent') }),
  });

  type SignInFormValues = z.infer<typeof signInSchema>;

  // Detect confirmation error params in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
    const hasError = urlParams.has('error') || hashParams.has('error');
    
    if (hasError) {
      setConfirmationError(true);
      setVerificationMessage(null);
      navigate('/sign-in', { replace: true });
    }
  }, [navigate]);

  // Countdown timer for confirmation error resend button (60s)
  useEffect(() => {
    if (!confirmationError || !isCountdownActive) return;
    if (countdown <= 0) {
      setIsCountdownActive(false);
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, isCountdownActive, confirmationError]);

  // Countdown timer for unconfirmed email panel
  useEffect(() => {
    if (!isUnconfirmedCountdownActive) return;
    if (unconfirmedCountdown <= 0) {
      setIsUnconfirmedCountdownActive(false);
      return;
    }
    const timer = setTimeout(() => setUnconfirmedCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [unconfirmedCountdown, isUnconfirmedCountdownActive]);

  // Handle resend confirmation email (for failed link panel)
  const handleResendConfirmation = async () => {
    if (!resendEmail.trim()) return;
    setResendLoading(true);
    setResendErrorKey(null);
    setResendErrorMsg(null);
    setResendSuccess(false);

    try {
      // Pre-check: is the email already confirmed?
      const { data: isConfirmed } = await supabase.rpc('is_email_confirmed', { p_email: resendEmail.trim() });
      if (isConfirmed) {
        setResendErrorKey('auth.emailAlreadyConfirmed');
        // Auto-reset to sign-in form after 5 seconds
        setTimeout(() => {
          setConfirmationError(false);
          setResendEmail('');
          setResendErrorKey(null);
          setResendErrorMsg(null);
          setResendSuccess(false);
          setCountdown(60);
          setIsCountdownActive(true);
        }, 5000);
        return;
      }

      const { error } = await supabase.auth.resend({ type: 'signup', email: resendEmail.trim(), options: { emailRedirectTo: `${window.location.origin}/sign-in` } });
      if (error) {
        setResendErrorMsg(translateSupabaseError(error, t));
      } else {
        setResendSuccess(true);
        setCountdown(60);
        setIsCountdownActive(true);
        toast({ title: t('auth.resendSuccess') });

        setTimeout(() => {
          setConfirmationError(false);
          setResendEmail('');
          setResendErrorKey(null);
          setResendErrorMsg(null);
          setResendSuccess(false);
          setCountdown(60);
          setIsCountdownActive(true);
        }, 5000);
      }
    } catch (err: any) {
      setResendErrorMsg(translateSupabaseError(err, t));
    } finally {
      setResendLoading(false);
    }
  };

  // Handle resend from the unconfirmed panel
  const handleUnconfirmedResend = async () => {
    setUnconfirmedResendLoading(true);
    setUnconfirmedResendErrorKey(null);
    setUnconfirmedResendErrorMsg(null);
    setUnconfirmedResendSuccess(false);

    try {
      // Pre-check: is the email already confirmed?
      const { data: isConfirmed } = await supabase.rpc('is_email_confirmed', { p_email: unconfirmedPanel.email });
      if (isConfirmed) {
        setUnconfirmedResendErrorKey('auth.emailAlreadyConfirmedSignIn');
        setTimeout(() => {
          setUnconfirmedPanel({ visible: false, email: '', recentlySent: false, secondsUntilResend: 0 });
          setUnconfirmedResendErrorKey(null);
          setUnconfirmedResendErrorMsg(null);
          setUnconfirmedResendSuccess(false);
        }, 5000);
        return;
      }

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: unconfirmedPanel.email,
        options: { emailRedirectTo: `${window.location.origin}/sign-in` }
      });
      if (error) {
        setUnconfirmedResendErrorMsg(translateSupabaseError(error, t));
      } else {
        setUnconfirmedResendSuccess(true);
        setUnconfirmedCountdown(60);
        setIsUnconfirmedCountdownActive(true);
        // Update panel message to "new email sent"
        setUnconfirmedPanel(prev => ({ ...prev, recentlySent: false }));
        toast({ title: t('auth.resendSuccess') });
      }
    } catch (err: any) {
      setUnconfirmedResendErrorMsg(translateSupabaseError(err, t));
    } finally {
      setUnconfirmedResendLoading(false);
    }
  };

  // Clean up verification tokens from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasVerificationTokens = urlParams.has('token_hash') || urlParams.has('type') || urlParams.has('access_token') || urlParams.has('code') || urlParams.has('refresh_token');
    
    if (hasVerificationTokens) {
      if (urlParams.get('type') === 'signup') {
        setVerificationMessage(t('auth.emailVerifiedSignIn'));
      }
      navigate('/sign-in', { replace: true });
    }
  }, [navigate, t]);

  useEffect(() => {
    if (!loading && isAuthenticated && userRole) {
      setTimeout(() => {
        if (userRole === 'admin') {
          navigate('/admin-dashboard', { replace: true });
        } else if (userRole === 'user') {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/error', { 
            replace: true,
            state: { 
              title: t('error.profileInvalidTitle'), 
              message: t('error.profileInvalidMessage') 
            }
          });
        }
      }, 100);
    }
  }, [isAuthenticated, navigate, loading, location.state, t, userRole]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const form = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values: SignInFormValues) => {
    setIsLoading(true);
    setGeneralError(null);
    setUnconfirmedPanel({ visible: false, email: '', recentlySent: false, secondsUntilResend: 0 });
    setUnconfirmedResendSuccess(false);
    setUnconfirmedResendErrorKey(null);
    setUnconfirmedResendErrorMsg(null);

    const { success, error, errorCode } = await signIn(values.email, values.password);
    
    setIsLoading(false);
    
    if (success) {
      console.log("Login successful, redirect will be handled by useEffect");
    } else if (errorCode === 'emailNotConfirmed') {
      // Check when the last confirmation email was sent
      try {
        const { data: sentAt } = await supabase.rpc('get_confirmation_sent_at', { p_email: values.email });
        const now = new Date();
        const THREE_MINUTES_MS = 3 * 60 * 1000;

        if (sentAt) {
          const sentAtDate = new Date(sentAt);
          const diffMs = now.getTime() - sentAtDate.getTime();

          if (diffMs < THREE_MINUTES_MS) {
            // Less than 3 minutes ago — link still likely valid, just ask to check inbox
            const secondsRemaining = Math.ceil((THREE_MINUTES_MS - diffMs) / 1000);
            setUnconfirmedPanel({ visible: true, email: values.email, recentlySent: true, secondsUntilResend: secondsRemaining });
            setUnconfirmedCountdown(secondsRemaining);
            setIsUnconfirmedCountdownActive(true);
          } else {
            // 3+ minutes — auto-send new email
            const { error: resendErr } = await supabase.auth.resend({
              type: 'signup',
              email: values.email,
              options: { emailRedirectTo: `${window.location.origin}/sign-in` }
            });
            if (!resendErr) {
              setUnconfirmedPanel({ visible: true, email: values.email, recentlySent: false, secondsUntilResend: 0 });
              setUnconfirmedCountdown(60);
              setIsUnconfirmedCountdownActive(true);
            } else {
              setGeneralError(error || t('auth.supabase.genericError'));
            }
          }
        } else {
          // No sentAt (shouldn't happen for unconfirmed) — show generic error
          setGeneralError(error || t('auth.supabase.genericError'));
        }
      } catch {
        setGeneralError(error || t('auth.supabase.genericError'));
      }
    } else {
      setGeneralError(error || t('auth.supabase.genericError'));
    }
  };

  // While loading auth or already authenticated (redirect pending), show minimal screen
  if (loading || isAuthenticated) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900" />
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div ref={pageTopRef} className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900">
        <div className="absolute inset-0 bg-black/30 z-0"></div>
        <div className="absolute top-0 right-1/3 w-80 h-80 rounded-full bg-indigo-500/5 blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 w-64 h-64 rounded-full bg-purple-500/10 blur-3xl"></div>
        
        <div className="relative z-10 container mx-auto px-4 pt-28 pb-16">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <img 
                src="/logo_1_inverted_no_background.png"
                alt="The Club Logo" 
                className="h-16 w-auto" 
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-montserrat font-bold text-white mb-2">
              {t('auth.welcomeBack')}
            </h1>
            <p className="text-2xl md:text-2xl text-gray-200 pt-6">
              {t('auth.welcomeMessage')}
            </p>
          </div>

          <div className="max-w-lg mx-auto glassmorphism-dark p-8 rounded-xl">
            {/* Failed confirmation link panel (from URL error) */}
            {confirmationError && (
              <div className="bg-white/5 border border-white/10 rounded-md p-4 mb-4">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-neutral-300">{t('auth.confirmationFailed')}</p>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-neutral-400 mb-1 block">{t('auth.enterEmailToResend')}</label>
                    <Input
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400"
                    />
                  </div>
                  
                  <Button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={isCountdownActive || resendLoading || !resendEmail.trim()}
                    className="w-full bg-finance-800 hover:bg-finance-900 disabled:opacity-50"
                  >
                    {resendLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('common.loading')}
                      </span>
                    ) : isCountdownActive ? (
                      <span className="flex items-center justify-center gap-2">
                        <Mail className="h-4 w-4" />
                        {t('auth.resendIn').replace('{{seconds}}', String(countdown))}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Mail className="h-4 w-4" />
                        {t('auth.resendConfirmationEmail')}
                      </span>
                    )}
                  </Button>
                  
                  {resendSuccess && (
                    <div className="bg-green-900/20 border border-green-800 text-green-200 rounded-md p-2 text-sm">
                      {t('auth.resendSuccess')}
                    </div>
                  )}
                  {(resendErrorKey || resendErrorMsg) && (
                    <div className={`rounded-md p-2 text-sm ${resendErrorKey === 'auth.emailAlreadyConfirmed' ? 'bg-blue-900/20 border border-blue-700 text-blue-200' : 'bg-red-900/20 border border-red-700 text-red-200'}`}>
                      {resendErrorKey ? t(resendErrorKey) : resendErrorMsg}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!confirmationError && (
              <>
                {/* Unconfirmed email panel (from login attempt) */}
                {unconfirmedPanel.visible && (
                  <div className="bg-white/5 border border-white/10 rounded-md p-4 mb-4">
                    <div className="flex items-start gap-3 mb-3">
                      <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-neutral-300">
                        <p className="font-medium mb-1">
                          {t('auth.confirmationSentTo')} <span className="text-white">{unconfirmedPanel.email}</span>
                        </p>
                        <p>
                          {unconfirmedResendSuccess
                            ? t('auth.confirmationNewSent')
                            : unconfirmedPanel.recentlySent
                              ? t('auth.confirmationRecentlySent')
                              : t('auth.confirmationNewSent')}
                        </p>
                      </div>
                    </div>

                    <Button
                      type="button"
                      onClick={handleUnconfirmedResend}
                      disabled={isUnconfirmedCountdownActive || unconfirmedResendLoading}
                      className="w-full bg-finance-800 hover:bg-finance-900 disabled:opacity-50"
                    >
                      {unconfirmedResendLoading ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('common.loading')}
                        </span>
                      ) : isUnconfirmedCountdownActive ? (
                        <span className="flex items-center justify-center gap-2">
                          <Mail className="h-4 w-4" />
                          {t('auth.resendIn').replace('{{seconds}}', String(unconfirmedCountdown))}
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Mail className="h-4 w-4" />
                          {t('auth.resendConfirmationEmail')}
                        </span>
                      )}
                    </Button>

                    {unconfirmedResendSuccess && (
                      <div className="bg-green-900/20 border border-green-800 text-green-200 rounded-md p-2 text-sm mt-3">
                        {t('auth.resendSuccess')}
                      </div>
                    )}
                    {(unconfirmedResendErrorKey || unconfirmedResendErrorMsg) && (
                      <div className={`rounded-md p-2 text-sm mt-3 ${unconfirmedResendErrorKey === 'auth.emailAlreadyConfirmedSignIn' ? 'bg-blue-900/20 border border-blue-700 text-blue-200' : 'bg-red-900/20 border border-red-700 text-red-200'}`}>
                        {unconfirmedResendErrorKey ? t(unconfirmedResendErrorKey) : unconfirmedResendErrorMsg}
                      </div>
                    )}
                  </div>
                )}

                {verificationMessage && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 rounded-md p-3 mb-4 text-sm">
                    {verificationMessage}
                  </div>
                )}
                {generalError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 rounded-md p-3 mb-4 text-sm">
                    {generalError}
                  </div>
                )}
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-neutral-300">{t('auth.email')}</FormLabel>
                          <FormControl>
                            <Input 
                              type="email" 
                              placeholder={t('auth.emailPlaceholder')}
                              autoComplete="email"
                              className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400"
                              {...field}
                              onChange={(e) => {
                                const sanitized = enhancedInputValidator.sanitizeText(e.target.value, 100);
                                field.onChange(sanitized);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-neutral-300">{t('auth.password')}</FormLabel>
                          <FormControl>
                            <PasswordInput 
                              autoComplete="current-password"
                              className="bg-white/10 border-white/20 text-white placeholder:text-neutral-400"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="text-right">
                      <Link
                        to={getLocalizedPath('/forgot-password', language)}
                        className="text-base text-purple-400 hover:text-purple-300"
                      >
                        {t('auth.forgotPassword')}
                      </Link>
                    </div>
                    
                    <Button type="submit" className="w-full bg-finance-800 hover:bg-finance-900" disabled={isLoading}>
                      {isLoading ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('common.loading')}
                        </span>
                      ) : (
                        t('auth.signIn')
                      )}
                    </Button>
                  </form>
                </Form>
                
                <div className="text-center text-base text-neutral-300 mt-6">
                  <span>{t('auth.noAccount')}</span>{' '}
                  <Link to={getLocalizedPath('/sign-up', language)} className="text-purple-400 hover:text-purple-300 font-medium">
                    {t('auth.signUp')}
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SignIn;
